import classNames from "classnames";
import Flickity from "flickity-imagesloaded";
import React from "react";
import { loadWistiaPlayer } from "tsi-common-react/src/utils/wistia";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

export interface ICarouselCell {
    imageURL: string;
    imageTitle: string;
    videoSRC: string;
    label: string;
}

const pauseAllVideos = (): void => {
    const window = getWindow();
    if (!window) {
        return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wq = (window as any)._wq;
    if (!wq) {
        return;
    }
    wq.push({
        id: "_all",
        onReady: () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const allVideos = (window as any).Wistia.api.all();
            for (let i = 0; i < allVideos.length; i++) {
                allVideos[i].pause();
            }
        },
    });
};

// ADA - Remove and Set tabindex attributes for any carousel cells that are not selected
const setCellAttributes = (selectedCell: number): void => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const cells = document.querySelectorAll(
        ".slideshow--default .slideshow__cell",
    );
    Array.from(cells).map((cell, index) => {
        if (index === selectedCell) {
            cell.removeAttribute("tabindex");
        } else {
            cell.setAttribute("tabindex", "-1");
        }
    });
};

const initFlickity = (
    carousel: HTMLDivElement,
    selectedCell: number,
    setSelectedCell: (index: number) => void,
): Flickity => {
    const carouselFlickity = new Flickity(carousel, {
        cellSelector: ".slideshow__cell",
        cellAlign: "center",
        pageDots: false,
        adaptiveHeight: true,
        imagesLoaded: true,
    });

    carouselFlickity.select(selectedCell, true, true);
    setCellAttributes(selectedCell);

    carouselFlickity.on("change", () => {
        setSelectedCell(carouselFlickity.selectedIndex);
        pauseAllVideos();
        setCellAttributes(carouselFlickity.selectedIndex);
    });

    return carouselFlickity;
};

const buildCarouselCells = (images: ICarouselCell[]): React.ReactNode => {
    return images.map((img, i) => {
        const imageVideo = img.videoSRC ? (
            <span
                key={i}
                dangerouslySetInnerHTML={{ __html: img.videoSRC }}
            ></span>
        ) : (
            <img key={i} src={img.imageURL} alt={img.imageTitle} />
        );
        return (
            <div key={i} className="slideshow__cell">
                <div className="lead-in">
                    <h4>
                        <span className="slideshow__label type_x1point5">
                            {img.label}
                        </span>
                    </h4>
                </div>
                {imageVideo}
            </div>
        );
    });
};

const buildCarouselDots = (
    images: ICarouselCell[],
    selectedCell: number,
    onSelectCell: (
        cellIndex: number,
        event: React.MouseEvent<HTMLElement>,
    ) => void,
): React.ReactNode => {
    const dots = images.map((img, index) => {
        const classes = classNames({
            "is-selected": index === selectedCell,
        });
        const counter = index + 1;
        // TODO(i18n): img.label comes from data and should already be translated by Django backend, so this is likely correct as-is, but verify that img.label is translated before reaching this component
        const label = t`Slide ${counter} of ${images.length} - ${img.label}`;
        return (
            <button
                key={`${index}`}
                className={classes}
                onClick={(event) => onSelectCell(index, event)}
                aria-current={index === selectedCell}
                aria-label={label}
            ></button>
        );
    });
    return <div className="slideshow__dots">{dots}</div>;
};

export const FlagshipCarousel = (props: {
    images: ICarouselCell[];
}): React.ReactNode => {
    const [selectedCell, setSelectedCell] = React.useState<number>(1);
    const carouselRef = React.useRef<HTMLDivElement | null>(null);
    const carouselFlickityRef = React.useRef<Flickity | null>(null);

    React.useEffect(() => {
        const carouselElem = carouselRef.current;
        if (carouselElem) {
            carouselFlickityRef.current = initFlickity(
                carouselElem,
                selectedCell,
                setSelectedCell,
            );
        }
        loadWistiaPlayer();

        return () => {
            if (carouselFlickityRef.current) {
                carouselFlickityRef.current.destroy();
            }
        };
    }, []);

    React.useEffect(() => {
        if (carouselFlickityRef.current) {
            carouselFlickityRef.current.select(selectedCell);
        }
    }, [selectedCell]);

    const handleSelectCell = (
        cellIndex: number,
        event: React.MouseEvent<HTMLElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }
        setSelectedCell(cellIndex);
    };

    return (
        <>
            <div className="slideshow slideshow--default" ref={carouselRef}>
                {buildCarouselCells(props.images)}
            </div>
            {buildCarouselDots(props.images, selectedCell, handleSelectCell)}
        </>
    );
};
